import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const alignmentTrainingComponent: ComponentDef = {
  id: 'alignment-training',
  name: 'Alignment Training',
  description: 'Align models with human preferences: RLHF/PPO reward optimization, DPO/IPO/SimPO reference-free objectives, KTO unpaired feedback, ORPO odds-ratio tuning, REINFORCE policy gradient, and RLOO leave-one-out baselines.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Method',
      type: 'select',
      default: 'dpo',
      options: [
        { label: 'DPO (Direct Preference Optimisation)', value: 'dpo' },
        { label: 'IPO (Identity Preference Optimisation)', value: 'ipo' },
        { label: 'SimPO (Simple Preference Optimisation)', value: 'simpo' },
        { label: 'KTO (Kahneman–Tversky Optimisation)', value: 'kto' },
        { label: 'ORPO (Odds Ratio Preference Optimisation)', value: 'orpo' },
        { label: 'RLHF + PPO', value: 'ppo' },
        { label: 'REINFORCE', value: 'reinforce' },
        { label: 'RLOO (leave-one-out baseline)', value: 'rloo' },
        { label: 'GRPO', value: 'grpo' },
        { label: 'Online DPO', value: 'online_dpo' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant || 'dpo');

    const g = new GraphBuilder();

    if (variant === 'dpo') {
      // DPO: log ratio of policy vs reference on chosen/rejected pairs
      g.input('prompt', [seq_len, d_model]);
      g.input('chosen', [seq_len, d_model]);
      g.input('rejected', [seq_len, d_model]);

      g.linear('policy', 'prompt', [d_model, d_model]);
      g.linear('ref_model', 'prompt', [d_model, d_model]);

      g.activation('log_pi_chosen', 'policy', 'log π_θ(y_w | x)');
      g.activation('log_pi_rejected', 'policy', 'log π_θ(y_l | x)');
      g.activation('log_ref_chosen', 'ref_model', 'log π_ref(y_w | x)');
      g.activation('log_ref_rejected', 'ref_model', 'log π_ref(y_l | x)');

      g.add('ratio_chosen', 'log_pi_chosen', 'log_ref_chosen');
      g.add('ratio_rejected', 'log_pi_rejected', 'log_ref_rejected');
      g.scale('margin', 'ratio_chosen', 'β·(log ratio_w − log ratio_l)');
      g.activation('loss', 'margin', '−log σ(margin)  [DPO loss]');
      g.output('out', 'loss');

    } else if (variant === 'ipo') {
      // IPO: replaces log-sigmoid with squared error to prevent distribution collapse
      g.input('prompt', [seq_len, d_model]);
      g.linear('policy', 'prompt', [d_model, d_model]);
      g.linear('ref_model', 'prompt', [d_model, d_model]);

      g.activation('log_ratio_w', 'policy', 'log π_θ(y_w)/π_ref(y_w)');
      g.activation('log_ratio_l', 'policy', 'log π_θ(y_l)/π_ref(y_l)');
      g.add('h_diff', 'log_ratio_w', 'log_ratio_l');
      // IPO loss: (h(y_w,y_l) − 1/2β)²  — identity mapping avoids overconfidence
      g.scale('centered', 'h_diff', '− 1/(2β)');
      g.activation('loss', 'centered', '(h − 1/2β)²  [IPO loss]');
      g.output('out', 'loss');

    } else if (variant === 'simpo') {
      // SimPO: no reference model; length-normalised log-prob margin
      g.input('prompt', [seq_len, d_model]);
      g.input('chosen', [seq_len, d_model]);
      g.input('rejected', [seq_len, d_model]);

      g.linear('policy', 'prompt', [d_model, d_model]);
      g.activation('log_pi_chosen', 'policy', 'log π_θ(y_w | x)');
      g.activation('log_pi_rejected', 'policy', 'log π_θ(y_l | x)');
      // Length normalisation: divide by |y| so model can't game by being verbose
      g.scale('norm_chosen', 'log_pi_chosen', '÷ |y_w|');
      g.scale('norm_rejected', 'log_pi_rejected', '÷ |y_l|');
      g.add('margin', 'norm_chosen', 'norm_rejected');
      g.scale('margin_gamma', 'margin', '− γ  (target margin)');
      g.activation('loss', 'margin_gamma', '−log σ(β·margin − γ)  [SimPO loss]');
      g.output('out', 'loss');

    } else if (variant === 'kto') {
      // KTO: works on unpaired (x, y, label) data — no chosen/rejected pairs needed
      g.input('prompt', [seq_len, d_model]);
      g.input('response', [seq_len, d_model]);
      g.input('label', [1]);   // desirable=1 / undesirable=0

      g.linear('policy', 'prompt', [d_model, d_model]);
      g.linear('ref_model', 'prompt', [d_model, d_model]);

      g.activation('log_ratio', 'policy', 'log π_θ(y|x) / π_ref(y|x)');
      g.scale('kl_term', 'log_ratio', 'KL[π_θ || π_ref]  (per-batch estimate)');
      // Separate sigmoid heads for desirable vs undesirable
      g.activation('v_desired', 'log_ratio', 'σ(β·log_ratio − KL)  [desirable]');
      g.activation('v_undesired', 'log_ratio', 'σ(−β·log_ratio + KL)  [undesirable]');
      g.add('kto_loss', 'v_desired', 'v_undesired');
      g.output('out', 'kto_loss');

    } else if (variant === 'orpo') {
      // ORPO: no reference model; combines SFT cross-entropy with an odds-ratio penalty
      g.input('prompt', [seq_len, d_model]);
      g.input('chosen', [seq_len, d_model]);
      g.input('rejected', [seq_len, d_model]);

      g.linear('policy', 'prompt', [d_model, d_model]);
      // SFT NLL loss on chosen response (standard cross-entropy)
      g.activation('nll_chosen', 'policy', 'NLL(y_w | x)  [SFT term]');
      // Log probability of chosen and rejected
      g.activation('log_pi_chosen', 'policy', 'log π_θ(y_w | x)');
      g.activation('log_pi_rejected', 'policy', 'log π_θ(y_l | x)');
      // Odds = P / (1 - P); log-odds ratio = log π_w/(1-π_w) − log π_l/(1-π_l)
      g.scale('odds_chosen', 'log_pi_chosen', 'log[π_w / (1 − π_w)]');
      g.scale('odds_rejected', 'log_pi_rejected', 'log[π_l / (1 − π_l)]');
      g.add('log_odds_ratio', 'odds_chosen', 'odds_rejected');
      g.activation('or_loss', 'log_odds_ratio', '−log σ(log_odds_ratio)  [OR penalty]');
      // Combined loss: SFT + λ × OR penalty (no reference model needed)
      g.add('total_loss', 'nll_chosen', 'or_loss');
      g.output('out', 'total_loss');

    } else if (variant === 'reinforce') {
      // REINFORCE: policy gradient with reward baseline
      // ∇J = E[Σ_t ∇log π_θ(a_t|s_t) · (R_t - b)]
      g.input('prompt', [seq_len, d_model]);

      // Policy model (actor)
      g.linear('actor', 'prompt', [d_model, d_model]);
      g.activation('action_logits', 'actor', 'π_θ(a | s)');
      g.softmax('action_probs', 'action_logits', -1);

      // Sample action and compute log probability
      g.activation('log_pi', 'action_probs', 'log π_θ(a_t | s_t)');

      // Reward model scores the generated response
      g.linear('reward_model', 'prompt', [d_model, 1]);
      g.activation('reward', 'reward_model', 'R(s, a)  [reward model]');

      // KL penalty to keep policy close to reference
      g.linear('ref_model', 'prompt', [d_model, d_model]);
      g.activation('kl_penalty', 'ref_model', '−β KL[π_θ || π_ref]');
      g.add('adjusted_reward', 'reward', 'kl_penalty');

      // Baseline: running mean of rewards (variance reduction)
      g.scale('baseline', 'adjusted_reward', 'b = E[R]  (running mean)');
      g.add('advantage', 'adjusted_reward', 'baseline');

      // Policy gradient: ∇log π · (R - b)
      g.mul('pg_loss', 'log_pi', 'advantage');
      g.scale('reinforce_loss', 'pg_loss', '−E[∇log π · (R − b)]  [REINFORCE]');
      g.output('out', 'reinforce_loss');

    } else if (variant === 'rloo') {
      // RLOO: leave-one-out baseline instead of separate value function
      // For K samples, baseline for sample i = mean of all other samples
      g.input('prompt', [seq_len, d_model]);

      // Policy model generates K responses
      g.linear('actor', 'prompt', [d_model, d_model]);
      g.activation('action_logits', 'actor', 'π_θ(a | s)');
      g.softmax('action_probs', 'action_logits', -1);
      g.activation('log_pi', 'action_probs', 'log π_θ(a_t | s_t)');

      // Reward model scores each of K responses
      g.linear('reward_model', 'prompt', [d_model, 1]);
      g.activation('reward_k', 'reward_model', 'R_k  [reward for sample k]');

      // Leave-one-out baseline: b_k = (Σ_{j≠k} R_j) / (K-1)
      g.scale('sum_rewards', 'reward_k', 'Σ R_j (all samples)');
      g.scale('loo_baseline', 'sum_rewards', '(Σ R_j − R_k) / (K−1)  [LOO baseline]');

      // Advantage: R_k - LOO baseline (unbiased, lower variance than REINFORCE)
      g.add('advantage', 'reward_k', 'loo_baseline');

      // KL penalty to regularize toward reference
      g.linear('ref_model', 'prompt', [d_model, d_model]);
      g.activation('kl_penalty', 'ref_model', '−β KL[π_θ || π_ref]');
      g.add('adj_advantage', 'advantage', 'kl_penalty');

      // Policy gradient loss
      g.mul('pg_loss', 'log_pi', 'adj_advantage');
      g.scale('rloo_loss', 'pg_loss', '−E[∇log π · (R_k − b_LOO)]  [RLOO loss]');
      g.output('out', 'rloo_loss');

    } else if (variant === 'grpo') {
      // GRPO: Group Relative Policy Optimization
      // Generate G responses for same prompt, score with reward model,
      // compute group-relative advantages (normalize within group),
      // then PPO-style clipped update without a separate value model.
      g.input('prompt', [seq_len, d_model]);

      // Policy model generates G responses
      g.linear('actor', 'prompt', [d_model, d_model]);
      g.activation('action_logits', 'actor', 'π_θ(a | s)');
      g.softmax('action_probs', 'action_logits', -1);
      g.activation('log_pi', 'action_probs', 'log π_θ(a_t | s_t)');

      // Reward model scores each of G responses
      g.linear('reward_model', 'prompt', [d_model, 1]);
      g.activation('reward_g', 'reward_model', 'R_g  [reward for sample g]');

      // Group-relative normalization: advantage = (R_g - mean(R)) / std(R)
      g.scale('group_mean', 'reward_g', 'mean(R_g)  [group mean]');
      g.add('centered', 'reward_g', 'group_mean');
      g.scale('advantage', 'centered', '÷ std(R_g)  [group-relative advantage]');

      // Old policy for importance ratio (PPO-style clipped update)
      g.linear('old_policy', 'prompt', [d_model, d_model]);
      g.activation('old_logits', 'old_policy', 'π_old(a | s)');
      g.activation('ratio', 'action_probs', 'r_t(θ) = π_θ / π_old');
      g.scale('clipped_ratio', 'ratio', 'clip(r, 1−ε, 1+ε)');

      // Clipped objective: min(r·A, clip(r)·A)  — no separate value model needed
      g.mul('obj_unclipped', 'ratio', 'advantage');
      g.mul('obj_clipped', 'clipped_ratio', 'advantage');
      g.activation('grpo_obj', 'obj_unclipped', 'min(r·A, clip·A)  [GRPO clip]');

      // KL penalty to regularize toward reference
      g.linear('ref_model', 'prompt', [d_model, d_model]);
      g.activation('kl_penalty', 'ref_model', '−β KL[π_θ || π_ref]');
      g.add('total_loss', 'grpo_obj', 'kl_penalty');
      g.output('out', 'total_loss');

    } else if (variant === 'online_dpo') {
      // Online DPO: iteratively generates preference pairs from the current policy
      // itself (no static dataset), then applies DPO loss on generated pairs.
      g.input('prompt', [seq_len, d_model]);

      // Current policy (iteratively updated each round)
      g.linear('policy', 'prompt', [d_model, d_model]);

      // Self-play: current policy generates chosen/rejected pairs
      g.activation('gen_chosen', 'policy', 'π_θ(y_w | x)  [self-generated chosen]');
      g.activation('gen_rejected', 'policy', 'π_θ(y_l | x)  [self-generated rejected]');

      // Self-play scoring: reward model / LLM-as-judge scores generated pairs
      g.linear('reward_model', 'prompt', [d_model, 1]);
      g.activation('score_chosen', 'reward_model', 'R(y_w)  [self-play score]');
      g.activation('score_rejected', 'reward_model', 'R(y_l)  [self-play score]');

      // Reference model (frozen snapshot of policy at start of iteration)
      g.linear('ref_model', 'prompt', [d_model, d_model]);
      g.activation('log_ref_chosen', 'ref_model', 'log π_ref(y_w | x)');
      g.activation('log_ref_rejected', 'ref_model', 'log π_ref(y_l | x)');

      // DPO loss on generated pairs (same as offline DPO but on self-generated data)
      g.activation('log_pi_chosen', 'policy', 'log π_θ(y_w | x)');
      g.activation('log_pi_rejected', 'policy', 'log π_θ(y_l | x)');
      g.add('ratio_chosen', 'log_pi_chosen', 'log_ref_chosen');
      g.add('ratio_rejected', 'log_pi_rejected', 'log_ref_rejected');
      g.scale('margin', 'ratio_chosen', 'β·(log ratio_w − log ratio_l)');
      g.activation('loss', 'margin', '−log σ(margin)  [Online DPO loss]');
      g.output('out', 'loss');

    } else {
      // PPO (default fallback)
      g.input('prompt', [seq_len, d_model]);

      // Policy model (actor)
      g.linear('actor', 'prompt', [d_model, d_model]);
      g.activation('action_logits', 'actor', 'π_θ(a | s)');
      g.softmax('action_probs', 'action_logits', -1);

      // Reference / old policy for importance ratio
      g.linear('old_policy', 'prompt', [d_model, d_model]);
      g.activation('old_logits', 'old_policy', 'π_old(a | s)');
      g.activation('ratio', 'action_probs', 'r_t(θ) = π_θ / π_old');

      // Reward model
      g.linear('reward_model', 'prompt', [d_model, 1]);
      g.activation('reward', 'reward_model', 'r(s, a)');

      // Value function (critic) + GAE advantage
      g.linear('value_fn', 'prompt', [d_model, 1]);
      g.activation('advantage', 'value_fn', 'GAE: Â_t = Σ(γλ)^k δ_{t+k}');
      g.scale('kl_penalty', 'action_probs', '−β KL[π_θ||π_ref]');
      g.add('reward_kl', 'reward', 'kl_penalty');

      // Clipped PPO objective: min(r·A, clip(r, 1-ε, 1+ε)·A)
      g.scale('clipped_ratio', 'ratio', 'clip(r, 1−ε, 1+ε)');
      g.mul('obj_unclipped', 'ratio', 'advantage');
      g.mul('obj_clipped', 'clipped_ratio', 'advantage');
      g.activation('ppo_obj', 'obj_unclipped', 'min(r·A, clip·A)  [PPO-clip]');

      // Value loss + entropy bonus
      g.activation('value_loss', 'value_fn', '(V_θ − V_target)²');
      g.activation('entropy', 'action_probs', 'H[π_θ]  (entropy bonus)');
      g.add('total_loss', 'ppo_obj', 'value_loss');
      g.output('out', 'total_loss');
    }

    return g.build();
  }
};
